import * as React from 'react';
import * as PropTypes from 'prop-types';
import { classNames, ZIndexContext } from '@progress/kendo-react-common';
import { validatePackage } from '@progress/kendo-react-common';
import { packageMetadata } from './../package-metadata';
import { Popup } from '@progress/kendo-react-popup';
import { PopoverActionsBar } from './PopoverActionsBar';
var DEFAULT_POPOVER_ZINDEX = 12000;
var ZINDEX_POPOVER_STEP = 2000;
var CALLOUT_SIZE = 15;
var calloutPositions = {
    top: 'k-callout-s',
    left: 'k-callout-e',
    bottom: 'k-callout-n',
    right: 'k-callout-w'
};
var calloutFlippedPositions = {
    top: 'k-callout-n',
    left: 'k-callout-w',
    bottom: 'k-callout-s',
    right: 'k-callout-e'
};
var popupAlignByPosition = {
    top: { vertical: 'bottom', horizontal: 'center' },
    left: { vertical: 'center', horizontal: 'right' },
    bottom: { vertical: 'top', horizontal: 'center' },
    right: { vertical: 'center', horizontal: 'left' }
};
var anchorAlignByPosition = {
    top: { vertical: 'top', horizontal: 'center' },
    left: { vertical: 'center', horizontal: 'left' },
    bottom: { vertical: 'bottom', horizontal: 'center' },
    right: { vertical: 'center', horizontal: 'right' }
};
var getActionBar = function (children) {
    return React.Children.toArray(children).filter(function (child) { return child && child.type === PopoverActionsBar; });
};
var getContent = function (children) {
    return React.Children.toArray(children).filter(function (child) {
        return child && child.type !== PopoverActionsBar;
    });
};
/**
 * Represents the Popover component.
 */
export var Popover = React.forwardRef(function (props, ref) {
    validatePackage(packageMetadata);
    var callout = props.callout, position = props.position, title = props.title, children = props.children, show = props.show, anchor = props.anchor, appendTo = props.appendTo, offset = props.offset, animate = props.animate, positionMode = props.positionMode, scale = props.scale, popoverClass = props.popoverClass, className = props.className, id = props.id, style = props.style, collision = props.collision, contentStyle = props.contentStyle, onPosition = props.onPosition, onClose = props.onClose, onOpen = props.onOpen;
    var _a = React.useState(false), flipped = _a[0], setFlipped = _a[1];
    var isVertical = position === 'top' || position === 'bottom';
    var margin = props.margin || {
        vertical: callout && isVertical ? CALLOUT_SIZE : 0,
        horizontal: callout && !isVertical ? CALLOUT_SIZE : 0
    };
    var popupAlign = popupAlignByPosition[position];
    var anchorAlign = anchorAlignByPosition[position];
    var zindexContext = React.useContext(ZIndexContext);
    var currentZindex = zindexContext ? zindexContext + ZINDEX_POPOVER_STEP : DEFAULT_POPOVER_ZINDEX;
    var popoverRef = React.useRef(null);
    React.useImperativeHandle(popoverRef, function () {
        return { props: props };
    });
    React.useImperativeHandle(ref, function () { return popoverRef.current; });
    var handlePosition = React.useCallback(function (e) {
        setTimeout(function () { return setFlipped(e.flipped); });
        if (onPosition) {
            var event_1 = { target: popoverRef.current, flipped: e.flipped, fitted: e.fitted };
            onPosition.call(undefined, event_1);
        }
    }, [onPosition, setFlipped]);
    var handleOpen = React.useCallback(function () {
        if (onOpen) {
            var event_2 = { target: popoverRef.current };
            onOpen.call(undefined, event_2);
        }
    }, [onOpen]);
    var handleClose = React.useCallback(function () {
        if (onClose) {
            var event_3 = { target: popoverRef.current };
            onClose.call(undefined, event_3);
        }
    }, [onClose]);
    var basePopupClass = { 'k-popover': true, 'k-popup': false };
    if (Array.isArray(popoverClass)) {
        popoverClass.forEach(function (currentClass) { return basePopupClass[currentClass] = true; });
    }
    else if (typeof popoverClass === 'object') {
        Object.keys(popoverClass)
            .forEach(function (key) {
            basePopupClass[key] = popoverClass[key];
        });
    }
    else if (popoverClass) {
        basePopupClass[popoverClass] = true;
    }
    var calloutClass = flipped ? calloutFlippedPositions[position] : calloutPositions[position];
    return (React.createElement(ZIndexContext.Provider, { value: currentZindex },
        React.createElement(Popup, { id: id, animate: animate, collision: collision, anchor: anchor, offset: offset, margin: margin, popupAlign: popupAlign, anchorAlign: anchorAlign, appendTo: appendTo, show: show, scale: scale, positionMode: positionMode, style: style, className: className, popupClass: basePopupClass, onOpen: handleOpen, onClose: handleClose, onPosition: handlePosition },
            callout && position &&
                (React.createElement("div", { className: classNames('k-popover-callout', calloutClass) })),
            title && (React.createElement("div", { className: 'k-popover-header' }, title)),
            React.createElement("div", { className: "k-popover-body", style: contentStyle }, getContent(children)),
            getActionBar(children))));
});
var defaultProps = {
    callout: true,
    position: 'top',
    collision: {
        horizontal: 'none',
        vertical: 'none'
    }
};
Popover.propTypes = {
    callout: PropTypes.bool,
    title: PropTypes.string,
    position: PropTypes.oneOf(['top', 'left', 'bottom', 'right']),
    show: PropTypes.bool,
    animate: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.shape({
            openDuration: PropTypes.number,
            closeDuration: PropTypes.number
        })
    ]),
    anchor: function (props) {
        var anchor = props.anchor;
        if (anchor && typeof anchor.nodeType !== 'number') {
            return new Error('Invalid prop `anchor` supplied to `Kendo React Popover`. Validation failed.');
        }
        return null;
    },
    appendTo: function (props) {
        var element = props.appendTo;
        if (element && typeof element.nodeType !== 'number') {
            return new Error('Invalid prop `appendTo` supplied to `Kendo React Popover`. Validation failed.');
        }
        return null;
    },
    positionMode: PropTypes.oneOf([
        'fixed',
        'absolute'
    ]),
    scale: PropTypes.number,
    offset: PropTypes.shape({
        left: PropTypes.number,
        top: PropTypes.number
    }),
    children: PropTypes.oneOfType([
        PropTypes.element,
        PropTypes.node
    ]),
    className: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.object
    ]),
    id: PropTypes.string,
    popoverClass: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
        PropTypes.object
    ]),
    style: PropTypes.object,
    onClose: PropTypes.func,
    onPosition: PropTypes.func,
    onOpen: PropTypes.func
};
Popover.defaultProps = defaultProps;
Popover.displayName = 'KendoReactPopover';
